import { Component, inject, OnInit } from '@angular/core';
import {
  NbAutocompleteModule,
  NbFormFieldModule,
  NbInputModule,
  NbListModule,
  NbOptionModule,
  NbToggleModule,
} from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IInvestmentWizard } from '../wizard-interface';
import { ReactiveFormsModule } from '@angular/forms';
import { InvestmentReasonsService } from '../../../../../@core/utils';
import { KPI } from '../../../../../@core/api';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-investment-reason',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    NbToggleModule,
    NbAutocompleteModule,
    NbOptionModule,
  ],
  template: `
    <div class="d-flex flex-row">
      <ng-container *ngIf="information$ | async as information">
        <nb-list>
          @for (ir of information.irs; track ir.id) {
            <nb-list-item [class.selected]="selected(ir)" (click)="selectReason(ir)" style="cursor: pointer;">
              {{ ir.name }}
            </nb-list-item>
          }
        </nb-list>
      </ng-container>
    </div>
  `,
  styleUrls: ['../wizard-index.component.scss'],
})
export class InvestmentReasonComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'INVESTMENT_REASON_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);

  // dependencies
  private reasons$: BehaviorSubject<KPI.InvestmentReason[]>;
  private wizardService = inject(InvestmentWizardService);
  private irService = inject(InvestmentReasonsService);
  private irs$ = this.irService.investmentReasons$;

  // data for template
  information$: Observable<{ reasons: KPI.InvestmentReason[], irs: KPI.InvestmentReason[] }>;

  ngOnInit(): void {
    this.reasons$ = new BehaviorSubject(this.state() || []);
    this.information$ = combineLatest([
      this.reasons$,
      this.irs$,
    ]).pipe(
      map(([reasons, irs]) => {
        return { reasons, irs };
      }),
      tap(({ irs, reasons }) => {
        if (irs.length === 0) {
          console.warn('no investment reason, going to next page');
          this.wizardService.next();
          return;
        }
        const mandatory = this.wizardService.investmentReasonMandatory();
        if (mandatory && reasons.length === 0) {
          this.wizardService.blockCurrentStep('Please select a tag to continue');
        } else {
          this.wizardService.unblockCurrentStep();
        }
      }),
    );
  }

  selectReason(ir: KPI.InvestmentReason) {
    const updated = this.selected(ir) ? [] : [ir];
    this.reasons$.next(updated);
    this.wizardService.setState('INVESTMENT_REASON_SELECTION', updated);
  }

  selected(ir: KPI.InvestmentReason): boolean {
    return this.reasons$.value.map(i => i.id).includes(ir.id);
  }
}
